/* =============================================================================
   ics-UI FACADE - the jQuery bridge generator. GENERATED FROM THE REGISTRY,
   never hand-written per widget: adding a component is one Registry.add() and
   its bridge appears. The calling convention:
     $('#x').icsDatebox({...})            options object -> (re)initialize
     $('#x').icsDatebox('getValue')       method string  -> dispatch on instance
   Getters return the value; setters keep jQuery chaining (fluent component
   methods return the instance - the bridge folds that back to the jQuery set).
   NAME RULE: where a component's plain name would collide with a name another
   script on the page already defines (tooltip, modal), the ics-prefixed name is
   the authoritative one and the plain name is NOT shadowed.
   ============================================================================= */
import { Component } from '../core/component.js';
import { Registry } from '../services/registry.js';

/**
 * Build the bridge function for one component class (not yet assigned to
 * any $.fn name - callers decide the name(s)).
 * @param {*} $            the jQuery object (never assumed global here)
 * @param {string} name    PascalCase widget name from the Registry
 * @param {Function} Ctor  Component subclass
 * @param {string|null} [selector]  the entry's auto-mount selector: a method-string
 *   call on an UNMOUNTED element that matches it mounts first, then dispatches -
 *   a body-inline `$('#btn').icsButton('disable')` may run before the ready-queue
 *   mount and must still find a working widget (the mount is idempotent)
 */
export function makeBridge($, name, Ctor, selector) {
  return function (arg, ...rest) {
    if (typeof arg === 'string') {                          // method-string dispatch
      if (!this.length) {
        // PHANTOM CONTRACT: a statement against an ABSENT element is a SILENT
        // no-op - never a warning. Views legitimately address controls a given
        // page state does not render, and warning would spam the console on
        // every load. A getter yields undefined; anything else returns the
        // (empty) set, so a chained call still works.
        return /^(get|is)/.test(arg) ? undefined : this;
      }
      const resolve = (el) => {
        let i = Component.get(el);
        if (!i && selector && el.matches && el.matches(selector)) i = new Ctor(el);  // mount-on-demand (declared markup, early caller)
        return i;
      };
      const inst = resolve(this[0]);
      if (!inst) {
        /* element EXISTS but is unmounted - a real bug, so STAY LOUD.
           ⚠ But answer a GETTER with `undefined`, exactly as the empty-set contract
           above does. Returning `this` here would hand the caller a jQuery SET,
           which is truthy - and the universal caller idiom is
             `var row = $('#g').datagrid('getSelected'); if (row == null) { warn; return; }`
           so the guard would never fire and a DOM element would flow onward as if
           it were a business object: silent data corruption in a save path. A
           getter that answers `undefined` turns the same mistake into a loud
           null-check failure at the call site. */
        console.warn('[ics] $.fn.ics' + name + ": '" + arg + "' called on unmounted element", this[0]);
        return /^(get|is)/.test(arg) ? undefined : this;
      }
      const member = inst[arg];
      if (typeof member !== 'function') {
        // property-getter dispatch: `panel('options')` & co. return the LIVE
        // member (the mutable options bag callers read and write)
        if (member !== undefined) return member;
        /* FAIL LOUDLY: a caller bug. The error is TAGGED so a caller that has a
           reason to tolerate this one case can recognise it precisely instead of
           string-matching the message or swallowing every error. Exactly one
           does: the datetime entry's INHERITED names (`textbox` / `spinner`),
           where a call is written against a DIFFERENT widget's API and an
           unrecognised name is tolerated rather than a bug (index.datetime.js).
           Nothing else may use the tag to go quiet. */
        const err = new Error('[ics] $.fn.ics' + name + ": unknown method '" + arg + "'");
        err.icsUnknownMethod = arg;
        err.icsComponent = name;
        throw err;
      }
      const out = member.apply(inst, rest);
      // fluent methods return the INSTANCE -> fold back to the jQuery set;
      // everything else - INCLUDING undefined - transports verbatim, because a
      // getter on an empty widget must deliver undefined and call sites compare
      // against it. Family rule: every mutator method returns `this`.
      if (out !== inst) return out;                         // GETTER: reads the first element only
      /* MUTATOR ON A MULTI-ELEMENT SET: a setter runs on EVERY matched element, the
         way a jQuery setter does. A read-only lock like
           $('#btnPickStore, #btnPickIssueStyle, #btnPickProduct, ...').icsButton('disable')
         must disable all of them; dispatching on `this[0]` alone would silently leave
         the rest looking enabled. Getters keep first-element semantics above; only
         mutators iterate. */
      for (let i = 1; i < this.length; i++) {
        const other = resolve(this[i]);
        if (!other) { console.warn('[ics] $.fn.ics' + name + ": '" + arg + "' called on unmounted element", this[i]); continue; }
        const m = other[arg];
        if (typeof m === 'function') m.apply(other, rest);
      }
      return this;
    }
    // options object: init when fresh; on an ALREADY-init element it is a
    // merge + re-render (relabelling a button from Add to Update, say) -
    // the option() multi-key set drives each _optionChanged hook
    return this.each((_, el) => {
      const inst = Component.get(el);
      if (inst) inst.option(arg || {});
      else new Ctor(el, arg || {});
    });
  };
}

/** classic form: assign the bridge under $.fn.ics<Name> */
export function registerJQuery($, name, Ctor) {
  const e = Registry.get(name);
  $.fn['ics' + name] = makeBridge($, name, Ctor, e ? e.selector : null);
}

/**
 * Bridge every facade-enabled Registry entry. Called once by the bundle entry
 * at load; safe to call again after a late registration (re-bridging an
 * existing name just reassigns the same generated function). An entry that
 * declares a claimName ALSO answers under that plain jQuery name (e.g.
 * $.fn.datebox -> the icsDatebox bridge) IF the name is still free; it never
 * shadows a name another script already defined (the NAME RULE above). The
 * Button entry declares NO claimName: `$.fn.icsButton` is its only call name.
 * @param {*} $ jQuery
 */
/** claimName is a string OR an array (Timespinner answers both `timespinner`
    and the plain `spinner`). */
export function claimNamesOf(e) {
  if (!e || !e.claimName) return [];
  return Array.isArray(e.claimName) ? e.claimName : [e.claimName];
}

export function registerAllFacades($) {
  if (!$ || !$.fn) { console.warn('[ics] registerAllFacades: no jQuery'); return 0; }
  let n = 0;
  for (const e of Registry.all()) {
    const claims = claimNamesOf(e);
    if (!e.facade && !claims.length) continue;
    const bridge = makeBridge($, e.name, e.Ctor, e.selector);
    if (e.facade) { $.fn['ics' + e.name] = bridge; n++; }
    // facade:false + claimName = the plain name ONLY: the component's `ics`
    // name is a different call (Window: `$.fn.icsWindow` is the
    // application-level open/close, so this bridge must not claim it)
    for (const ln of claims) { if (!$.fn[ln]) $.fn[ln] = bridge; }
  }
  return n;
}

/* ================================================================================
   WHY THE PLAIN NAMES ARE CLAIM-IF-FREE
   ================================================================================
   Every claimName above is taken CLAIM-IF-FREE (`!$.fn[name]`): on a page that
   ALSO loads another implementation of those names, that implementation's
   semantics are the ones the page's views were written against, and taking the
   name unconditionally would silently swap the implementation under working
   screens. Every shipped page may be such a page, so the per-family entries
   wrap an occupied name as a DISPATCHER (own markup -> ours, everything else ->
   the handler installed before it) rather than claiming it. There is no
   unconditional claim mode in this library.

   NOTE ON `Window` (the one entry that stays facade:false):
   `$.fn.icsWindow` is NOT this component's bridge - it is the application-level
   open/close call defined in the same module (`components/ics-window.js`, whose
   'close' may close the topmost window rather than its receiver) and keeps that
   name. The widget is reached through `$.fn.window`. Window deliberately never
   flips to facade:true, which would clobber `$.fn.icsWindow`. That split is
   PERMANENT by design.
   ================================================================================ */
